/**
 * DAS (Digital Asset Standard) Client
 *
 * Calls DAS methods (`getAsset`, `getAssetBatch`) through the
 * `solana-rpc-proxy` edge function. Post Pack 1 the proxy routes Triton
 * primary with Alchemy fallback; provider selection is server-side and
 * transparent to this client.
 *
 * IMPORTANT: used ONLY for enrichment, never for discovery. All token
 * discovery happens against the local `launched_tokens` table.
 */

import { getSolanaEndpoint } from '@/lib/solana/config';
import { debug } from "@/lib/debug";

const getRpcUrl = (): string => {
  const rpcUrl = getSolanaEndpoint();
  if (!rpcUrl) {
    console.warn('[DAS] No RPC URL found, enrichment will be skipped');
    return '';
  }
  return rpcUrl;
};

export interface DasAsset {
  id: string;
  content: {
    json_uri?: string;
    metadata?: {
      name?: string;
      symbol?: string;
      description?: string;
    };
    links?: {
      image?: string;
      external_url?: string;
    };
  };
  authorities?: Array<{
    address: string;
    scopes: string[];
  }>;
  compression?: {
    compressed: boolean;
  };
  grouping?: Array<{
    group_key: string;
    group_value: string;
  }>;
  ownership?: {
    owner: string;
  };
  token_info?: {
    symbol?: string;
    supply?: number;
    decimals?: number;
    token_program?: string;
  };
}

/**
 * Fetch a single asset's metadata via DAS.
 * Used to enrich token data that may be missing image/symbol in our DB.
 */
export async function getAsset(mint: string): Promise<DasAsset | null> {
  const rpcUrl = getRpcUrl();
  if (!rpcUrl) {
    return null;
  }

  try {
    debug.trace('[DAS] Fetching asset:', mint);

    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'memelaunch-das',
        method: 'getAsset',
        params: { id: mint },
      }),
    });

    if (!response.ok) {
      console.error('[DAS] HTTP error:', response.status);
      return null;
    }

    const result = await response.json();

    if (result.error) {
      console.error('[DAS] RPC error:', result.error);
      return null;
    }

    debug.trace('[DAS] Asset fetched successfully');
    return result.result as DasAsset;
  } catch (error) {
    console.error('[DAS] Error fetching asset:', error);
    return null;
  }
}

/**
 * Batch fetch multiple assets via DAS.
 * More efficient than making N individual calls.
 */
export async function getAssetBatch(mints: string[]): Promise<Map<string, DasAsset>> {
  const rpcUrl = getRpcUrl();
  const results = new Map<string, DasAsset>();

  if (!rpcUrl || mints.length === 0) {
    return results;
  }

  try {
    debug.trace('[DAS] Fetching batch of', mints.length, 'assets');

    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'memelaunch-das-batch',
        method: 'getAssetBatch',
        params: { ids: mints },
      }),
    });

    if (!response.ok) {
      console.error('[DAS] Batch HTTP error:', response.status);
      return results;
    }

    const result = await response.json();

    if (result.error) {
      console.error('[DAS] Batch RPC error:', result.error);
      return results;
    }

    if (Array.isArray(result.result)) {
      result.result.forEach((asset: DasAsset) => {
        if (asset?.id) {
          results.set(asset.id, asset);
        }
      });
    }

    debug.trace('[DAS] Batch fetched', results.size, 'assets');
    return results;
  } catch (error) {
    console.error('[DAS] Batch error:', error);
    return results;
  }
}

/**
 * Extract useful fields from a DAS asset response.
 */
export function extractAssetMetadata(asset: DasAsset | null): {
  name?: string;
  symbol?: string;
  imageUrl?: string;
  description?: string;
} {
  if (!asset) return {};

  return {
    name: asset.content?.metadata?.name,
    symbol: asset.content?.metadata?.symbol || asset.token_info?.symbol,
    imageUrl: asset.content?.links?.image,
    description: asset.content?.metadata?.description,
  };
}
